/**
 * Security utilities for sanitizing user-controlled content from BPMN XML.
 *
 * BPMN files may contain custom attributes (imageUrl, videoUrl, etc.) that
 * are rendered into the DOM. A crafted .bpmn file could embed malicious URLs
 * to track users, exfiltrate data, or trigger unwanted requests.
 */

const SAFE_IMAGE_RE = /^data:image\/(png|jpeg|jpg|gif|webp|bmp|svg\+xml);base64,/i;
const SAFE_VIDEO_RE = /^https:\/\//i;
const SHAREPOINT_HOST_RE = /^https:\/\/[a-z0-9-]+(\.[a-z0-9-]+)*\.sharepoint\.com\//i;
const MAX_ATTR_LENGTH = 100;
const MAX_DATA_URL_LENGTH = 20 * 1024 * 1024; // 20 MB
const MAX_VIDEO_URL_LENGTH = 4096; // SharePoint sharing tokens can be long

/**
 * Check if an image URL is safe to render.
 * Only data:image/... base64 URLs are allowed (the format produced by the editor).
 * External URLs (http/https) are rejected to prevent tracking/phishing.
 */
export function isSafeImageUrl(url) {
  return typeof url === 'string'
    && url.length <= MAX_DATA_URL_LENGTH
    && SAFE_IMAGE_RE.test(url);
}

/**
 * Check if a video URL is safe to load.
 * Only HTTPS URLs are allowed — no http, javascript, data, or blob.
 * Length limit accommodates SharePoint sharing links with embedded tokens.
 */
export function isSafeVideoUrl(url) {
  return typeof url === 'string'
    && url.length <= MAX_VIDEO_URL_LENGTH
    && SAFE_VIDEO_RE.test(url);
}

/**
 * Detects SharePoint / OneDrive sharing or file URLs that we want to
 * resolve via Microsoft Graph before handing them to the <video> element.
 *
 * Examples it should match:
 *   https://contoso.sharepoint.com/:v:/s/Site/EaBcDef...
 *   https://contoso.sharepoint.com/sites/Site/Shared%20Documents/clip.mp4
 *   https://contoso-my.sharepoint.com/personal/user_contoso_com/...
 */
export function isSharePointShareUrl(url) {
  return typeof url === 'string'
    && url.length <= MAX_VIDEO_URL_LENGTH
    && SHAREPOINT_HOST_RE.test(url);
}

/**
 * Detects legacy Microsoft Stream (Classic) URLs which we explicitly do
 * not support — stream.classic was deprecated by Microsoft.
 */
export function isLegacyStreamUrl(url) {
  return typeof url === 'string'
    && /^https:\/\/web\.microsoftstream\.com\//i.test(url);
}

/**
 * Encode a sharing URL into the Microsoft Graph "u!" format expected by
 * `GET /shares/{encoded}/driveItem`.
 *   1. base64-encode the UTF-8 URL bytes
 *   2. trim trailing '=' padding
 *   3. URL-safe alphabet: '+' → '-', '/' → '_'
 *   4. prefix with 'u!'
 *
 * See: https://learn.microsoft.com/graph/api/shares-get
 */
export function encodeShareUrl(url) {
  if (typeof url !== 'string' || !url) return '';
  // btoa works on binary strings — encodeURIComponent → unescape converts
  // a UTF-8 string into one btoa accepts. Browsers always have btoa.
  // eslint-disable-next-line no-undef
  const b64 = btoa(unescape(encodeURIComponent(url)));
  const safe = b64.replaceAll('+', '-').replaceAll('/', '_').replace(/=+$/, '');
  return `u!${safe}`;
}

/**
 * Sanitize a short text attribute (author, version, etc.).
 * Truncates to MAX_ATTR_LENGTH and strips control characters.
 */
export function sanitizeTextAttr(value) {
  if (typeof value !== 'string') return '';
  // Strip control characters (except tab, LF, CR)
  const cleaned = value.replaceAll(/[^\P{Cc}\t\n\r]/gu, '');
  return cleaned.slice(0, MAX_ATTR_LENGTH);
}

const MAX_COMMENT_LENGTH = 500;
const MAX_HISTORY_ENTRIES = 50;
// Ein komprimierter Stand wiegt in der Praxis unter 100 kB; die Schranke
// ist nur ein Riegel gegen manipulierte Dateien, keine Vorgabe.
const MAX_SNAPSHOT_LENGTH = 8 * 1024 * 1024;

/**
 * Sanitize a version comment string.
 * Strips control characters and truncates to MAX_COMMENT_LENGTH.
 */
export function sanitizeComment(value) {
  if (typeof value !== 'string') return '';
  const cleaned = value.replaceAll(/[^\P{Cc}\t\n\r]/gu, '');
  return cleaned.slice(0, MAX_COMMENT_LENGTH);
}

/**
 * Validate a version XML snapshot.
 * Must start with a valid XML/BPMN preamble. Strips control characters
 * but does NOT truncate (XML can be large).
 */
export function sanitizeXmlSnapshot(value) {
  if (typeof value !== 'string') return undefined;
  if (!value.startsWith('<?xml') && !value.startsWith('<bpmn') && !value.startsWith('<definitions')) {
    return undefined;
  }
  return value.replaceAll(/[^\P{Cc}\t\n\r]/gu, '');
}

/**
 * Validate versionHistory JSON.
 * Must be an array of { id?, version, author, date, comment?, xml? | xmlz?, images? } objects.
 * Keeps only the newest MAX_HISTORY_ENTRIES entries.
 * Returns sanitized array or empty array on failure.
 */
export function sanitizeVersionHistory(jsonString) {
  if (typeof jsonString !== 'string') return [];
  try {
    const parsed = JSON.parse(jsonString);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(entry => entry && typeof entry === 'object')
      .map(entry => {
        const sanitized = {
          version: sanitizeTextAttr(String(entry.version ?? '')),
          author: sanitizeTextAttr(String(entry.author ?? '')),
          date: sanitizeTextAttr(String(entry.date ?? '')),
        };
        // Die Kennung muss den Import überleben — ohne sie fällt der
        // Rücksprung auf den mehrdeutigen Vergleich über Version und Stand
        // zurück (siehe versionHistory.sameEntry).
        if (typeof entry.id === 'string' && /^[\w-]{1,64}$/.test(entry.id)) {
          sanitized.id = entry.id;
        }
        if (entry.comment) {
          sanitized.comment = sanitizeComment(String(entry.comment));
        }
        // Altbestand: Klartext-Stand, hier direkt prüfbar.
        if (entry.xml) {
          const xml = sanitizeXmlSnapshot(String(entry.xml));
          if (xml) sanitized.xml = xml;
        }
        // Neue Form: zlib+base64. Der Inhalt ist von hier aus nicht
        // einsehbar — geprüft werden Zeichenvorrat und Länge, gesäubert wird
        // beim Entpacken in versionHistory.readSnapshot.
        if (typeof entry.xmlz === 'string') {
          if (entry.xmlz.length <= MAX_SNAPSHOT_LENGTH
              && /^[A-Za-z0-9+/]+={0,2}$/.test(entry.xmlz)) {
            sanitized.xmlz = entry.xmlz;
          } else {
            // Sonst verschwaende der Stand lautlos — wie bei videoUrl und
            // imageUrl bleibt wenigstens eine Spur.
            console.warn(`Unbrauchbarer Versionsstand in v${entry.version} verworfen`);
          }
        }
        // Preserve image references (elementId → hash mapping)
        if (entry.images && typeof entry.images === 'object' && !Array.isArray(entry.images)) {
          const cleanImages = {};
          for (const [key, val] of Object.entries(entry.images)) {
            // Hash should be a hex string (SHA-256 = 64 chars)
            if (typeof val === 'string' && /^[a-f0-9]{1,128}$/i.test(val)) {
              cleanImages[sanitizeTextAttr(key)] = val;
            }
          }
          if (Object.keys(cleanImages).length > 0) {
            sanitized.images = cleanImages;
          }
        }
        return sanitized;
      })
      .slice(-MAX_HISTORY_ENTRIES); // keep newest, drop oldest
  } catch {
    return [];
  }
}

// --- Internal helpers for custom attribute access ---

/** Read a custom attribute with try/catch fallback to $attrs. */
function getCustomAttr(obj, name) {
  const key = 'custom:' + name;
  try {
    const val = obj.get(key);
    if (val) return val;
  } catch { /* moddle may not support .get() */ }
  return obj.$attrs?.[key];
}

/** Write a custom attribute with try/catch fallback to $attrs. */
function setCustomAttr(obj, name, value) {
  const key = 'custom:' + name;
  try { obj.set(key, value); } catch { /* fallback */ }
  if (obj.$attrs) obj.$attrs[key] = value;
}

/** Remove a custom attribute from both moddle and $attrs. */
function removeCustomAttr(obj, name) {
  const key = 'custom:' + name;
  try { obj.set(key, undefined); } catch { /* fallback */ }
  if (obj.$attrs) delete obj.$attrs[key];
}

/**
 * Sanitize all custom attributes on a BPMN element after import.
 * Clears unsafe values in-place.
 * Returns true if any attribute was sanitized (removed/modified).
 */
export function sanitizeElementAttributes(element) {
  const bo = element.businessObject;
  if (!bo) return false;

  const imageUrl = getCustomAttr(bo, 'imageUrl');
  if (imageUrl && !isSafeImageUrl(imageUrl)) {
    removeCustomAttr(bo, 'imageUrl');
    console.warn(`Unsafe imageUrl removed from element "${bo.name || bo.id}"`);
    return true;
  }
  return false;
}

/**
 * Sanitize definitions-level custom attributes after import.
 */
export function sanitizeDefinitionsAttributes(definitions) {
  if (!definitions) return;
  sanitizeVideoUrl(definitions);
  sanitizeHistory(definitions);
  sanitizeImageStore(definitions);
  sanitizeTextAttrs(definitions);
}

function sanitizeVideoUrl(definitions) {
  const videoUrl = getCustomAttr(definitions, 'videoUrl');
  if (videoUrl && !isSafeVideoUrl(videoUrl)) {
    removeCustomAttr(definitions, 'videoUrl');
    console.warn('Unsafe videoUrl removed from definitions');
  }
}

/**
 * Ist der Rohtext der Historie ueberhaupt lesbar?
 *
 * Dieselbe Regel, die versionHistory.parseHistory anwendet — die
 * Schreibpfade und der Sanitizer muessen sich einig sein, was „kaputt"
 * heisst. Absichtlich hier dupliziert statt importiert: versionHistory holt
 * sich sanitizeXmlSnapshot von hier, ein Import zurueck waere ein Ring.
 */
function isReadableHistory(raw) {
  try {
    return Array.isArray(JSON.parse(raw));
  } catch {
    return false;
  }
}

function sanitizeHistory(definitions) {
  const history = getCustomAttr(definitions, 'versionHistory');
  if (!history) return;
  // Unlesbares bleibt stehen. Es hier durch eine leere Liste zu ersetzen
  // verwirft die Historie im Speicher, und der naechste Speichervorgang macht
  // den Verlust dauerhaft. Lesen kann den Rohtext ohnehin niemand,
  // gefaehrlich wird er dadurch nicht.
  if (!isReadableHistory(history)) {
    console.warn('Versionshistorie ist unlesbar — sie bleibt unveraendert stehen.');
    return;
  }
  setCustomAttr(definitions, 'versionHistory', JSON.stringify(sanitizeVersionHistory(history)));
}

/**
 * Sanitize the imageStore attribute on definitions.
 * Must be a JSON object mapping hex hash strings to data:image URLs.
 */
function sanitizeImageStore(definitions) {
  const store = getCustomAttr(definitions, 'imageStore');
  if (!store) return;
  try {
    const parsed = JSON.parse(store);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      removeCustomAttr(definitions, 'imageStore');
      return;
    }
    const cleaned = {};
    for (const [hash, dataUrl] of Object.entries(parsed)) {
      // Hash must be a hex string
      if (typeof hash !== 'string' || !/^[a-f0-9]{1,128}$/i.test(hash)) continue;
      // Data URL must be a safe image
      if (typeof dataUrl === 'string' && isSafeImageUrl(dataUrl)) {
        cleaned[hash] = dataUrl;
      }
    }
    setCustomAttr(definitions, 'imageStore', JSON.stringify(cleaned));
  } catch {
    removeCustomAttr(definitions, 'imageStore');
  }
}

function sanitizeTextAttrs(definitions) {
  for (const attr of ['version', 'author', 'lastModified']) {
    const val = getCustomAttr(definitions, attr);
    if (typeof val === 'string') {
      const sanitized = sanitizeTextAttr(val);
      if (sanitized !== val) {
        setCustomAttr(definitions, attr, sanitized);
      }
    }
  }
}
